// src/pages/brand/BrandSettings.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import type { PlanKey } from "../../config/plans";

type MeResponse = {
  success: boolean;
  plan_id: PlanKey;
  credits_balance: number;
  allowedResolutions?: unknown;
  error?: string;
};

const MODE_KEY = "appcreativa_mode";

function prettyPlanName(plan: PlanKey): string {
  if (plan === "free") return "Free";
  if (plan === "basic") return "Basic";
  if (plan === "pro") return "Pro";
  return "Premium";
}

function normalizeAllowedResolutions(v: unknown): string[] {
  if (!v) return [];
  if (Array.isArray(v)) return v.map((x) => String(x)).filter(Boolean);
  return [];
}

export default function BrandSettings() {
  const { user, loading, getAccessToken, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [plan, setPlan] = useState<PlanKey>("free");
  const [credits, setCredits] = useState<number | null>(null);
  const [allowedResolutions, setAllowedResolutions] = useState<string[]>([]);
  const [busyLogout, setBusyLogout] = useState(false);

  // safe replace anti-loop
  const lastNavRef = useRef<string | null>(null);
  const safeReplace = useCallback(
    (to: string) => {
      const current = location.pathname + (location.search || "");
      if (current === to) return;
      if (lastNavRef.current === to) return;
      lastNavRef.current = to;
      navigate(to, { replace: true });
    },
    [navigate, location.pathname, location.search]
  );

  const loadMe = useCallback(async () => {
    setCredits(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        const p = ((user as any)?.plan as PlanKey) || "free";
        setPlan(p);
        setCredits(0);
        setAllowedResolutions([]);
        return;
      }

      const resp = await fetch("/api/me", {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = (await resp.json()) as MeResponse;

      if (!resp.ok || !data?.success) {
        const p = ((user as any)?.plan as PlanKey) || "free";
        setPlan(p);
        setCredits(0);
        setAllowedResolutions([]);
        return;
      }

      setPlan((data.plan_id as PlanKey) || (((user as any)?.plan as PlanKey) || "free"));
      setCredits(typeof data.credits_balance === "number" ? data.credits_balance : 0);
      setAllowedResolutions(normalizeAllowedResolutions(data.allowedResolutions));
    } catch {
      const p = ((user as any)?.plan as PlanKey) || "free";
      setPlan(p);
      setCredits(0);
      setAllowedResolutions([]);
    }
  }, [getAccessToken, user]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      safeReplace("/login");
      return;
    }
    loadMe();
  }, [loading, user, loadMe, safeReplace]);

  const planLabel = useMemo(() => prettyPlanName(plan), [plan]);

  const goUpgradeCredits = useCallback(() => {
    safeReplace("/upgrade?from=/brand/settings");
  }, [safeReplace]);

  const goUpgradePlan = useCallback(() => {
    // misma página /upgrade; si más adelante separas planes vs créditos,
    // aquí podrías usar /pricing o /upgrade?tab=plans
    safeReplace("/upgrade?from=/brand/settings");
  }, [safeReplace]);

  const goChooseMode = useCallback(() => {
    try {
      localStorage.removeItem(MODE_KEY);
    } catch {}
    safeReplace("/mode?choose=1");
  }, [safeReplace]);

  const handleLogout = useCallback(async () => {
    if (busyLogout) return;
    setBusyLogout(true);
    try {
      try {
        localStorage.removeItem(MODE_KEY);
      } catch {}
      await logout();
      safeReplace("/login");
    } finally {
      setBusyLogout(false);
    }
  }, [logout, safeReplace, busyLogout]);

  const showUpgradePlan = plan !== "premium";

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-sm text-gray-400">Cargando…</div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.25em] text-gray-400">BRAND MODE</div>
        <h1 className="text-3xl font-bold mt-2">Configuración</h1>
      </div>

      {/* Card: Plan */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          {/* Left: Plan info */}
          <div className="min-w-[280px]">
            <div className="text-xs uppercase tracking-[0.25em] text-gray-400">Plan actual</div>

            <div className="mt-3 text-lg font-semibold text-white">
              {planLabel} ·{" "}
              <span className="text-gray-200">créditos: {credits === null ? "…" : credits}</span>
            </div>

            {allowedResolutions.length > 0 && (
              <div className="mt-3 text-[12px] text-gray-400">
                Resoluciones permitidas:{" "}
                <span className="text-gray-200">{allowedResolutions.join(", ")}</span>
              </div>
            )}

            <button
              onClick={loadMe}
              className="mt-4 text-[11px] px-3 py-2 rounded-lg border border-white/10 hover:bg-white/10 transition"
              title="Refrescar plan/créditos"
            >
              🔄 Refrescar
            </button>
          </div>

          {/* Right: hint */}
          <div className="lg:ml-auto lg:max-w-[420px] flex lg:items-center lg:justify-end">
            <div className="text-[12px] text-gray-400 lg:text-right">
              Recomendación: itera en 720p/1080p y finaliza en 2k/4k/8k.
            </div>
          </div>
        </div>
      </div>

      {/* NEW: Upgrade Plan block */}
      <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-gray-400">Mejorar plan</div>
            <h2 className="mt-2 text-lg font-semibold text-white">
              Desbloquea resoluciones más altas y más capacidad
            </h2>
            <p className="mt-1 text-[12px] text-gray-400">
              Si quieres generar en 1080p, 2k, 4k o 8k con mayor velocidad y límites más amplios, sube de plan.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
            {showUpgradePlan ? (
              <button
                onClick={goUpgradePlan}
                className="w-full sm:w-auto min-w-[190px] px-6 py-3 rounded-xl text-sm font-semibold
                  border border-white/15 hover:bg-white/10 transition"
                title="Ver planes"
              >
                🚀 Mejorar plan
              </button>
            ) : (
              <div className="text-[12px] text-gray-300 bg-white/5 border border-white/10 px-4 py-3 rounded-xl">
                ✅ Ya estás en <span className="font-semibold">Premium</span>
              </div>
            )}

            <button
              onClick={goUpgradeCredits}
              className="w-full sm:w-auto min-w-[190px] px-6 py-3 rounded-xl text-sm font-semibold
                bg-gradient-to-r from-indigo-500 to-purple-500
                hover:from-indigo-400 hover:to-purple-400
                transition shadow-lg shadow-indigo-500/30"
              title="Comprar créditos"
            >
              💳 Comprar créditos
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-start lg:justify-end">
        <button
          onClick={goChooseMode}
          className="w-full sm:w-auto min-w-[190px] px-6 py-3 rounded-xl text-sm font-semibold
            border border-white/15 hover:bg-white/10 transition"
          title="Volver al selector de modo"
        >
          🔁 Cambiar modo
        </button>

        <button
          onClick={handleLogout}
          disabled={busyLogout}
          className={`w-full sm:w-auto min-w-[190px] px-6 py-3 rounded-xl text-sm font-semibold
            border border-white/15 transition
            ${busyLogout ? "opacity-60 cursor-not-allowed" : "hover:bg-white/10"}`}
          title="Cerrar sesión"
        >
          {busyLogout ? "Cerrando…" : "🚪 Cerrar sesión"}
        </button>
      </div>
    </div>
  );
}
